
import React, { useMemo, useState, useEffect } from 'react';
import { CalendarDays, Clock, ListChecks, ChevronDown, ChevronUp } from 'lucide-react';
import tenant from '../../config/tenant';


function findKey(row, patterns, exclude) {
  const keys = Object.keys(row || {});
  for (const pattern of patterns) {
    const hit = keys.find((k) => pattern.test(k) && !(exclude && exclude.test(k)));
    if (hit) return hit;
  }
  return null;
}

const VEHICLE_FIELD = /^v[_\s]?(in|out)$|vehicle/i;

function isBlank(v) {
  if (v === null || v === undefined) return true;
  const s = String(v).trim();
  return s === '' || s === '-' || s === '--' || /^null$/i.test(s) || /^(0{1,2}:0{2}(:0{2})?)$/.test(s);
}

function parseTimeMinutes(val) {
  if (isBlank(val)) return null;
  const str = String(val).trim();

  if (/\d{4}-\d{2}-\d{2}/.test(str) || /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(str)) {
    const d = new Date(str);
    if (!isNaN(d.getTime())) return d.getHours() * 60 + d.getMinutes();
  }

  const m = str.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM|am|pm)?/);
  if (m) {
    let h = parseInt(m[1], 10);
    const mins = parseInt(m[2], 10);
    const ap = m[4];
    if (ap) {
      const isPM = /pm/i.test(ap);
      if (isPM && h < 12) h += 12;
      if (!isPM && h === 12) h = 0;
    }
    return h * 60 + mins;
  }
  return null;
}

export function normaliseRow(row) {
  const dateKey    = findKey(row, [/^p_date$/i, /att.*date/i, /^date$/i, /date$/i], VEHICLE_FIELD);
  const statusKey   = findKey(row, [/^p_status$/i, /att.*status/i, /^status$/i, /day.*type/i]);
  const inKey       = findKey(row, [/^p_in$/i, /^in$/i, /in.*time/i, /time.*in/i, /check.*in/i], VEHICLE_FIELD);
  const outKey      = findKey(row, [/^p_out$/i, /^out$/i, /out.*time/i, /time.*out/i, /check.*out/i], VEHICLE_FIELD);
  const vinKey      = findKey(row, [/^vin$/i, /^v_in$/i, /vehicle.*in/i]);
  const voutKey     = findKey(row, [/^vout$/i, /^v_out$/i, /vehicle.*out/i]);
  const hoursKeyDoc = findKey(row, [/work.*hour/i, /total.*hour/i, /^hours$/i, /^p_hours$/i]);
  const otKey       = findKey(row, [/^ot/i, /overtime/i]);
  const shiftKey    = findKey(row, [/shift/i]);
  const remarksKey  = findKey(row, [/remark/i, /note/i, /reason/i]);

  const rawDate = dateKey ? row[dateKey] : null;
  let day = null;
  if (rawDate) {
    const d = new Date(rawDate);
    if (!isNaN(d.getTime())) day = d.getDate();
  }

  const inVal  = inKey  ? row[inKey]  : null;
  const outVal = outKey ? row[outKey] : null;
  const inMin  = parseTimeMinutes(inVal);
  const outMin = parseTimeMinutes(outVal);

  const hasIn  = !isBlank(inVal);

  let hours = null;
  if (hoursKeyDoc) {
    hours = Number(row[hoursKeyDoc]) || 0;
  } else if (inMin !== null && outMin !== null) {
    let diff = outMin - inMin;
    if (diff < 0) diff += 24 * 60; 
    hours = diff / 60;
  }

  const rawStatus = statusKey ? String(row[statusKey] ?? '').trim() : '';
  const statusLower = rawStatus.toLowerCase();

  let bucket;
  if (statusLower && (statusLower.includes('leave') || statusLower.includes('lv'))) bucket = 'leave';
  else if (statusLower && (statusLower.includes('holiday') || statusLower.includes('hol'))) bucket = 'holiday';
  else if (statusLower && statusLower.includes('week')) bucket = 'off';
  else if (!hasIn) bucket = 'absent';
  else if (inMin !== null && inMin > tenant.rules.lateThresholdMinutes) bucket = 'late';
  else bucket = 'present';

  return {
    raw: row,
    day,
    rawDate,
    bucket,
    status: rawStatus,
    inTime:  inVal,
    outTime: outVal,
    vin:     vinKey  ? row[vinKey]  : null,
    vout:    voutKey ? row[voutKey] : null,
    hours,
    ot:      otKey    ? Number(row[otKey]) || 0 : null,
    shift:   shiftKey   ? row[shiftKey]   : null,
    remarks: remarksKey ? row[remarksKey] : null,
    dateKey, vinKey, voutKey,
  };
}


const BUCKET_STYLE = {
  present: { label: 'Present', dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  late:    { label: 'Late',    dot: 'bg-amber-500',   chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  absent:  { label: 'Absent',  dot: 'bg-red-500',     chip: 'bg-red-50 text-red-700 border-red-200' },
  leave:   { label: 'Leave',   dot: 'bg-violet-500',  chip: 'bg-violet-50 text-violet-700 border-violet-200' },
  holiday: { label: 'Holiday', dot: 'bg-sky-500',     chip: 'bg-sky-50 text-sky-700 border-sky-200' },
  off:     { label: 'Day Off', dot: 'bg-gray-400',    chip: 'bg-gray-100 text-gray-500 border-gray-200' },
};

function daysInMonth(month) {
  if (!month) return 31;
  const [y, m] = month.split('-').map(Number);
  if (!y || !m) return 31;
  return new Date(y, m, 0).getDate();
}

function friendlyLabel(key, dateKey, vinKey, voutKey) {
  //if (key === dateKey || key === 'AttDate') return 'Date';
  if (key === vinKey)  return 'Vehicle IN';
  if (key === voutKey) return 'Vehicle OUT';
  if (key === 'InTime') return 'In Time';
  if (key === 'OutTime') return 'Out Time';
  if (key === 'ServiceNo') return 'Service No' 
  return key.replace(/^p_/i, '').replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim().replace(/\b\w/g, c => c.toUpperCase());
}

function formatCell(key, value, dateKey) {
  if (value === null || value === undefined || value === '') return '—';
  if (key === dateKey || key === 'AttDate') {
    const d = new Date(value);
    return !isNaN(d.getTime()) ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : String(value);
  }
  return String(value);
}

// ── Summary stat pill ─────────────────────────────────────────────────────────
function StatPill({ label, value, icon: Icon, colorClass }) {
  return (
    <div className="flex items-center gap-2.5 bg-white border border-gray-100 rounded-xl px-3.5 py-2.5 shadow-sm min-w-[110px]">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${colorClass}`}>
        <Icon size={15} />
      </div>
      <div className="min-w-0">
        <p className="text-lg font-extrabold text-gray-900 leading-none">{value}</p>
        <p className="text-[10px] text-gray-400 font-medium mt-0.5 truncate">{label}</p>
      </div>
    </div>
  );
}

export default function AttendanceCardView({ rows, month, highlightDay, onSelectMonth }) {

  const [showRaw, setShowRaw] = useState(true);

  useEffect(() => {
    setShowRaw(true);
  }, [rows, month]);

  const normalised = useMemo(() => (rows || []).map(normaliseRow), [rows]);

  const byDay = useMemo(() => {
    const m = {};
    normalised.forEach((r) => { if (r.day) m[r.day] = r; });
    return m;
  }, [normalised]);

  const stats = useMemo(() => {
    const counts = { present: 0, absent: 0, late: 0, leave: 0, holiday: 0, off: 0 };
    let totalHours = 0, totalOT = 0;
    normalised.forEach((r) => {
      if (counts[r.bucket] !== undefined) counts[r.bucket]++;
      totalHours += r.hours || 0;
      totalOT    += r.ot || 0;
    });
    return { counts, totalHours, totalOT };
  }, [normalised]);

  const totalDays = daysInMonth(month);
  const monthLabel = useMemo(() => {
    if (!month) return '';
    const [y, m] = month.split('-').map(Number);
    if (!y || !m) return month;
    return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  }, [month]);

  const allColumns = useMemo(() => {
    const set = new Set();
    (rows || []).forEach((r) => Object.keys(r || {}).forEach((k) => set.add(k)));
    return Array.from(set);
  }, [rows]);

  const sample = normalised[0] || {};

  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-14 text-center text-gray-400">
        <CalendarDays size={36} className="opacity-30 mb-3" />
        <p className="text-sm font-semibold text-gray-500">No attendance data</p>
        <p className="text-xs text-gray-400 mt-1">{monthLabel ? `for ${monthLabel}` : 'Try a different month'}</p>
        {onSelectMonth && (
          <div className="mt-4 flex items-center gap-2 flex-wrap justify-center">
            <span className="text-xs text-gray-500">Quick select month:</span>
            {['2026-04', '2026-05', '2026-08'].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => onSelectMonth(m)}
                className={`text-xs px-3 py-1 rounded-lg border font-semibold transition-colors
                  ${month === m
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-700 border-gray-200 hover:bg-slate-100'}`}
              >
                {m}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary stats */}
      <div className="flex flex-wrap gap-2.5">
        <StatPill label="Present"   value={stats.counts.present} icon={ListChecks}   colorClass="bg-emerald-50 text-emerald-600" />
        <StatPill label="Absent"    value={stats.counts.absent}  icon={ListChecks}   colorClass="bg-red-50 text-red-600" />
        <StatPill label="Late"      value={stats.counts.late}    icon={Clock}        colorClass="bg-amber-50 text-amber-600" />
        <StatPill label="Leave"     value={stats.counts.leave}   icon={CalendarDays} colorClass="bg-violet-50 text-violet-600" />
        <StatPill label="Total Hrs" value={stats.totalHours ? stats.totalHours.toFixed(1) : '0.0'} icon={Clock} colorClass="bg-blue-50 text-blue-600" />
        {/* <StatPill label="OT Hrs"    value={stats.totalOT ? stats.totalOT.toFixed(1) : '—'} icon={TimerReset} colorClass="bg-gray-100 text-gray-600" /> */}
      </div>

      {/* Calendar grid */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <p className="text-sm font-bold text-gray-800">{monthLabel}</p>
          <div className="flex items-center gap-2.5 text-[10px] text-gray-400 flex-wrap">
            {Object.entries(BUCKET_STYLE).map(([key, s]) => (
              <span key={key} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${s.dot}`} /> {s.label}</span>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {Array.from({ length: totalDays }, (_, i) => i + 1).map((day) => {
            const rec = byDay[day];
            const s = rec ? BUCKET_STYLE[rec.bucket] : null;
            const isSelected = highlightDay === day;
            return (
              <div
                key={day}
                id={isSelected ? 'attendance-selected-day' : undefined}
                title={rec ? `${s.label}${rec.inTime ? ` · In ${rec.inTime}` : ''}${rec.outTime ? ` · Out ${rec.outTime}` : ''}` : 'No record'}
                className={`relative rounded-lg border px-1.5 py-2 flex flex-col items-center justify-center gap-1 min-h-[52px] transition-colors
                  ${rec ? s.chip : 'bg-gray-50 text-gray-300 border-gray-100'}
                  ${isSelected ? 'ring-2 ring-slate-800 ring-offset-1' : ''}`}
              >
                <span className="text-[11px] font-semibold">{day}</span>
                {rec && <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Raw records (collapsible) — date-only DATE column, friendly Vehicle IN/OUT labels */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <button
          onClick={() => setShowRaw((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <span>View detailed records ({rows.length})</span>
          {showRaw ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {showRaw && (
          <div className="overflow-x-auto border-t border-gray-100">
            <table className="w-full text-xs min-w-[600px]">
              <thead className="bg-gray-50">
                <tr>
                  {allColumns.map((col) => (
                    <th key={col} className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      {friendlyLabel(col, sample.dateKey, sample.vinKey, sample.voutKey)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                    {allColumns.map((col) => (
                      <td key={col} className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {formatCell(col, r[col], normalised[i]?.dateKey)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
